/**
 * Supplier invoice — `/finance/supplier-invoices/$supplierInvoiceId`.
 *
 * The screen exists to answer one question: can this be paid yet? Posting is
 * refused while any finding is open (`ap.match_discrepancy_open`), so the Post
 * button is disabled — with the reason stated — until every finding is closed,
 * rather than offered and then rejected.
 *
 * Resolving and overriding are NOT synonyms and the screen keeps them apart:
 * resolve says the variance went away and the server re-evaluates the line
 * first (`ap.tolerance_exceeded` if it still stands); override says a human
 * accepted it, and at or above the branch threshold the finding stays open
 * until an approval lands (`ap.override_requires_approval`). Both demand a
 * written reason, because a finding that closes without one is
 * indistinguishable from one that was hidden.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@erp/ui";
import { ArrowLeft, RefreshCw } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { formatQuantity } from "@/lib/format";

import {
  matchSupplierInvoice,
  overrideMatchDiscrepancy,
  postSupplierInvoice,
  resolveMatchDiscrepancy,
  type MatchDiscrepancy,
  type SupplierInvoice,
} from "../api";
import { QUANTITY_SCALE } from "../decimal";
import {
  DefinitionList,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  StatusBadge,
} from "../components/fields";
import { financeKeys, usePartyOptions, useSupplierInvoice } from "../queries";
import { SUPPLIER_INVOICE_STATUS_VARIANTS } from "./supplier-invoices-page";

const DISCREPANCY_VARIANTS = {
  open: "destructive",
  resolved: "success",
  overridden: "warning",
} as const;

export interface SupplierInvoicePageProps {
  supplierInvoiceId?: string;
  onBack: () => void;
  canMatch?: boolean;
  canPost?: boolean;
  canResolve?: boolean;
  canOverride?: boolean;
  canReadParties?: boolean;
}

export function SupplierInvoicePage({
  supplierInvoiceId,
  onBack,
  canMatch = true,
  canPost = true,
  canResolve = true,
  canOverride = true,
  canReadParties = true,
}: SupplierInvoicePageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const invoiceQuery = useSupplierInvoice(supplierInvoiceId);
  const partiesQuery = usePartyOptions("supplier", canReadParties);
  const invoice = invoiceQuery.data;

  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [reasonError, setReasonError] = useState<string | null>(null);

  const partyName = useMemo(() => {
    if (invoice === undefined) return "";
    return (
      (partiesQuery.data ?? []).find((party) => party.id === invoice.party_id)?.display_name ??
      invoice.party_id
    );
  }, [invoice, partiesQuery.data]);

  const onDocumentChanged = (next: SupplierInvoice) => {
    queryClient.setQueryData(financeKeys.supplierInvoices.detail(next.id), next);
    void queryClient.invalidateQueries({ queryKey: financeKeys.supplierInvoices.all });
  };

  const matchMutation = useIdempotentMutation<SupplierInvoice, { supplierInvoiceId: string }>({
    mutationFn: (input, key) => matchSupplierInvoice(input.supplierInvoiceId, key),
    onSuccess: onDocumentChanged,
    onError: (error) => surfaceProblem(error, t),
  });

  const postMutation = useIdempotentMutation<SupplierInvoice, { supplierInvoiceId: string }>({
    mutationFn: (input, key) => postSupplierInvoice(input.supplierInvoiceId, key),
    onSuccess: onDocumentChanged,
    onError: (error) => surfaceProblem(error, t),
  });

  const resolveMutation = useIdempotentMutation<
    SupplierInvoice,
    { supplierInvoiceId: string; discrepancyId: string; reason: string }
  >({
    mutationFn: (input, key) =>
      resolveMatchDiscrepancy(
        input.supplierInvoiceId,
        input.discrepancyId,
        { reason: input.reason },
        key,
      ),
    onSuccess: (next, variables) => {
      setReasons((current) => ({ ...current, [variables.discrepancyId]: "" }));
      onDocumentChanged(next);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const overrideMutation = useIdempotentMutation<
    SupplierInvoice,
    { supplierInvoiceId: string; discrepancyId: string; reason: string }
  >({
    mutationFn: (input, key) =>
      overrideMatchDiscrepancy(
        input.supplierInvoiceId,
        input.discrepancyId,
        { reason: input.reason },
        key,
      ),
    onSuccess: (next, variables) => {
      setReasons((current) => ({ ...current, [variables.discrepancyId]: "" }));
      onDocumentChanged(next);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const decide = (discrepancy: MatchDiscrepancy, action: "resolve" | "override") => {
    if (invoice === undefined) return;
    const reason = (reasons[discrepancy.id] ?? "").trim();
    if (reason === "") {
      setReasonError(
        t(
          "finance.supplierInvoices.reasonRequired",
          "A finding cannot be closed without a written reason.",
        ),
      );
      return;
    }
    setReasonError(null);
    const variables = {
      supplierInvoiceId: invoice.id,
      discrepancyId: discrepancy.id,
      reason,
    };
    if (action === "resolve") resolveMutation.mutate(variables);
    else overrideMutation.mutate(variables);
  };

  if (invoiceQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">{t("app.loading", "Loading…")}</div>;
  }

  if (invoice === undefined) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <ProblemBanner>
          {t("finance.supplierInvoices.notFound", "This supplier invoice could not be loaded.")}
        </ProblemBanner>
        <div>
          <Button type="button" variant="outline" onClick={onBack}>
            {t("finance.supplierInvoices.backToList", "Back to supplier invoices")}
          </Button>
        </div>
      </div>
    );
  }

  const openFindings = invoice.discrepancies.filter((finding) => finding.status === "open");
  const postable = invoice.status === "matched" && openFindings.length === 0;
  const statusLabel = t(`finance.supplierInvoices.status.${invoice.status}`, invoice.status);

  const renderQuantity = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, QUANTITY_SCALE);
    } catch {
      return value;
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={invoice.document_no}
        description={t(
          "finance.supplierInvoices.detailSubtitle",
          "Supplier reference {{reference}} · {{supplier}}",
          { reference: invoice.supplier_invoice_number, supplier: partyName },
        )}
        actions={
          <>
            <Button type="button" variant="ghost" onClick={onBack}>
              <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden="true" />
              {t("finance.supplierInvoices.backToList", "Back to supplier invoices")}
            </Button>
            {canMatch ? (
              <Button
                type="button"
                variant="outline"
                loading={matchMutation.isPending}
                onClick={() => matchMutation.mutate({ supplierInvoiceId: invoice.id })}
              >
                <RefreshCw className="size-4" aria-hidden="true" />
                {t("finance.supplierInvoices.rematch", "Re-run match")}
              </Button>
            ) : null}
            {canPost ? (
              <Button
                type="button"
                disabled={!postable}
                loading={postMutation.isPending}
                onClick={() => postMutation.mutate({ supplierInvoiceId: invoice.id })}
              >
                {t("finance.supplierInvoices.post", "Post to ledger")}
              </Button>
            ) : null}
          </>
        }
      />

      <Card>
        <CardContent className="flex flex-col gap-4 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge
              status={invoice.status}
              label={statusLabel}
              variants={SUPPLIER_INVOICE_STATUS_VARIANTS}
            />
            {invoice.posted_at === null ? null : (
              <span className="text-sm text-muted-foreground">
                {t("finance.supplierInvoices.postedAt", "Posted")}{" "}
                <span dir="ltr">{invoice.posted_at}</span>
              </span>
            )}
          </div>
          <DefinitionList
            rows={[
              {
                label: t("finance.supplierInvoices.invoiceDate", "Invoice date"),
                value: <span dir="ltr">{invoice.invoice_date}</span>,
              },
              {
                label: t("finance.supplierInvoices.dueDate", "Due date"),
                value: <span dir="ltr">{invoice.due_date}</span>,
              },
              {
                label: t("finance.common.currency", "Currency"),
                value: <span dir="ltr">{invoice.currency}</span>,
              },
              {
                label: t("finance.supplierInvoices.subtotal", "Net"),
                value: <MoneyDisplay value={invoice.subtotal} />,
              },
              {
                label: t("finance.supplierInvoices.taxTotal", "Tax payable to supplier"),
                value: <MoneyDisplay value={invoice.tax_total} />,
              },
              {
                label: t("finance.supplierInvoices.grandTotal", "Total"),
                value: <MoneyDisplay value={invoice.grand_total} />,
              },
              {
                label: t("finance.supplierInvoices.amountPaid", "Paid"),
                value: <MoneyDisplay value={invoice.amount_paid} />,
              },
            ]}
          />
          {postable || !canPost ? null : (
            <NoticeBanner>
              {invoice.status === "posted" || invoice.status === "paid"
                ? t("finance.supplierInvoices.alreadyPosted", "This invoice has already posted.")
                : t(
                    "finance.supplierInvoices.postBlocked",
                    "Posting is refused while a match finding is open. Close every finding first.",
                  )}
            </NoticeBanner>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.supplierInvoices.linesSection", "Lines")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-start text-muted-foreground">
                <th scope="col" className="p-2 text-start">
                  #
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("finance.supplierInvoices.lineDescription", "Description")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.supplierInvoices.lineQuantity", "Quantity")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.supplierInvoices.lineUnitPrice", "Unit price")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.supplierInvoices.lineNet", "Net")}
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("finance.supplierInvoices.lineTax", "Tax")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.supplierInvoices.lineTotal", "Total")}
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line) => (
                <tr key={line.id} className="border-b">
                  <td className="p-2 tabular-nums">{line.line_number}</td>
                  <td className="p-2">{line.description}</td>
                  <td dir="ltr" className="p-2 text-end tabular-nums">
                    {renderQuantity(line.quantity)}
                  </td>
                  <td dir="ltr" className="p-2 text-end tabular-nums">
                    {renderQuantity(line.unit_price)}
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.line_net} />
                  </td>
                  <td className="p-2">
                    <span dir="ltr" className="font-mono text-xs">
                      {line.tax.code}
                    </span>{" "}
                    <span className="text-muted-foreground">
                      {t("finance.supplierInvoices.taxPercent", "{{percent}}%", {
                        percent: line.tax.percent,
                      })}
                    </span>{" "}
                    <MoneyDisplay value={line.tax.amount} />
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.line_total} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.supplierInvoices.findingsSection", "Match findings")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {invoice.discrepancies.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.supplierInvoices.noFindings", "The match found nothing to answer for.")}
            </p>
          ) : (
            invoice.discrepancies.map((finding) => {
              const line = invoice.lines.find(
                (candidate) => candidate.id === finding.invoice_line_id,
              );
              const busy =
                (resolveMutation.isPending || overrideMutation.isPending) &&
                (resolveMutation.variables?.discrepancyId === finding.id ||
                  overrideMutation.variables?.discrepancyId === finding.id);
              return (
                <div key={finding.id} className="flex flex-col gap-3 rounded-md border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge
                        status={finding.status}
                        label={t(
                          `finance.supplierInvoices.findingStatus.${finding.status}`,
                          finding.status,
                        )}
                        variants={DISCREPANCY_VARIANTS}
                      />
                      <span className="text-sm font-medium">
                        {t(`finance.supplierInvoices.findingKind.${finding.kind}`, finding.kind)}
                      </span>
                      {line === undefined ? null : (
                        <span className="text-sm text-muted-foreground">
                          {t(
                            "finance.supplierInvoices.findingLine",
                            "Line {{line}} · {{description}}",
                            {
                              line: line.line_number,
                              description: line.description,
                            },
                          )}
                        </span>
                      )}
                    </div>
                    <MoneyDisplay value={finding.variance_amount} />
                  </div>

                  <p className="text-sm text-muted-foreground">
                    {t(
                      "finance.supplierInvoices.findingValues",
                      "Expected {{expected}} · invoiced {{actual}}",
                      {
                        expected: finding.expected_value,
                        actual: finding.actual_value,
                      },
                    )}
                  </p>

                  {finding.status !== "open" ? (
                    <p className="text-sm">
                      {finding.resolution_ref === ""
                        ? t("finance.supplierInvoices.findingClosed", "Closed.")
                        : finding.resolution_ref}
                    </p>
                  ) : canResolve || canOverride ? (
                    <div className="flex flex-col gap-2">
                      <Label htmlFor={`finding-reason-${finding.id}`}>
                        {t("finance.supplierInvoices.findingReason", "Reason")}
                      </Label>
                      <Input
                        id={`finding-reason-${finding.id}`}
                        maxLength={200}
                        value={reasons[finding.id] ?? ""}
                        onChange={(event) =>
                          setReasons((current) => ({
                            ...current,
                            [finding.id]: event.target.value,
                          }))
                        }
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        {canResolve ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            loading={busy && resolveMutation.isPending}
                            onClick={() => decide(finding, "resolve")}
                          >
                            {t(
                              "finance.supplierInvoices.resolve",
                              "Resolve — the variance went away",
                            )}
                          </Button>
                        ) : null}
                        {canOverride ? (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            loading={busy && overrideMutation.isPending}
                            onClick={() => decide(finding, "override")}
                          >
                            {t(
                              "finance.supplierInvoices.override",
                              "Override — accept the variance",
                            )}
                          </Button>
                        ) : null}
                      </div>
                      {finding.approval_request_id === null ? null : (
                        <NoticeBanner>
                          {t(
                            "finance.supplierInvoices.overridePending",
                            "This override is with the approval workflow. The finding stays open until the decision lands.",
                          )}
                        </NoticeBanner>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
          {reasonError === null ? null : <ProblemBanner>{reasonError}</ProblemBanner>}
        </CardContent>
      </Card>
    </div>
  );
}
